import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import LottieView from "lottie-react-native"
import React, { useContext, useEffect, useRef, useState } from "react"
import { TouchableOpacity, View } from "react-native"
import { ScrollView } from "react-native-gesture-handler"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import {
    ChronometerCounter,
    PetalCounter,
    XPCount,
} from "../../../common/components/statistics/Counters"
import { CounterWrapper } from "../../../common/components/statistics/CounterWrapper"
import { skillSetsService } from "../../../data/services/skillSetsService"
import { getTodaysFinishedActivities } from "../../../data/utility/dataUtils"
import { ChampionImageM } from "../../champions/ChampionImageM"
import {
    getCelebrationMessage,
    getChampion,
    getIsFastCombo,
} from "./ActivityFinalizedHelpers"
import {DimensionsContext} from "@common/utils/mobileUtils";

const COUNTDOWN_DURATION = 12
const ANIMATION_START_DELAY = 500
const COUNTER_TYPES = {
    XP: "xp",
    PETALS: "petals",
    CHRONO: "chrono",
}

const ActivityFinalizedCongrats = (props) => {
    const {
        congratsData,
        activity,
        onContinuePress,
        // Écran de fin réutilisable par un appelant qui possède le sien
        // (session Sam) : il choisit la mascotte, le libellé du bouton, et
        // peut ajouter des sorties secondaires. Tous les défauts reproduisent
        // le comportement du flux standard à l'identique.
        championName = null,
        continueLabel = "ACTIVITÉ SUIVANTE",
        secondaryActions = [],
    } = props

    const { petalsGained = 0, userSkillSets } = congratsData
    const xPGained = activity?.xp_gained
    const chronoDuration = activity?.duration
    const dimensions = useContext(DimensionsContext)

    const [timerCanStart, setTimerCanStart] = useState(false)

    const [animationState, setAnimationState] = useState({
        [COUNTER_TYPES.XP]: {
            visible: false,
            completed: false,
            value: xPGained,
        },
        [COUNTER_TYPES.PETALS]: {
            visible: false,
            completed: false,
            value: petalsGained,
        },
        [COUNTER_TYPES.CHRONO]: {
            visible: false,
            completed: false,
            value: chronoDuration || 0,
        },
    })

    const fadeOpacity = useSharedValue(0)
    const fadeStyle = useAnimatedStyle(() => ({
        opacity: fadeOpacity.value,
    }))

    const lottieAnimationRef = useRef(null)

    const updateAnimationState = (type, updates) => {
        setAnimationState((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                ...updates,
            },
        }))
    }

    useEffect(() => {
        PlaySoundM.play(require("../../../../assets/sounds/finished.mp3"))

        const timer = setTimeout(() => {
            if (xPGained > 0) {
                updateAnimationState(COUNTER_TYPES.XP, { visible: true })
            } else if (petalsGained > 0) {
                updateAnimationState(COUNTER_TYPES.PETALS, { visible: true })
            } else {
                updateAnimationState(COUNTER_TYPES.CHRONO, { visible: true })
                // Sans XP NI pétales, aucun compteur ne rappellera
                // `handleCounterAnimationComplete` : le bloc du bouton
                // restait à `opacity: 0` pour toujours, et l'élève se
                // retrouvait sur un écran de fin SANS aucune sortie. Le
                // chrono, lui, n'a pas de fin d'animation à attendre.
                setTimerCanStart(true)
            }
        }, ANIMATION_START_DELAY)

        // Rien à marquer comme lu quand il n'y a pas d'évolution : l'appel
        // partait avec `skill_set_ids: undefined` et revenait en 500.
        const skillSetIds = userSkillSets?.map((ss) => ss?.id) ?? []
        if (skillSetIds.length > 0) {
            props.apiRequest(
                skillSetsService.markSkillSetEvolutionAsRead,
                {},
                { skill_set_ids: skillSetIds }
            )
        }

        return () => clearTimeout(timer)
    }, [])

    const handleCounterWrapperComplete = (type) => {
        if (type === COUNTER_TYPES.XP) {
            if (petalsGained > 0) {
                updateAnimationState(COUNTER_TYPES.PETALS, { visible: true })
            } else {
                updateAnimationState(COUNTER_TYPES.CHRONO, { visible: true })
            }
        } else if (type === COUNTER_TYPES.PETALS) {
            updateAnimationState(COUNTER_TYPES.CHRONO, { visible: true })
        }
    }

    const handleCounterAnimationComplete = (type) => {
        updateAnimationState(type, { completed: true })
        if (
            type === COUNTER_TYPES.PETALS ||
            (type === COUNTER_TYPES.XP && petalsGained <= 0)
        ) {
            setTimerCanStart(true)
        }
    }

    useEffect(() => {
        if (timerCanStart) {
            fadeOpacity.value = withTiming(1, {
                duration: 800,
                easing: Easing.out(Easing.ease),
            })
        }
    }, [timerCanStart])

    const champion = useRef(championName || getChampion(props.user)).current

    const finishedActivities = getTodaysFinishedActivities(props.user)

    const isFirstActivity = props.user?.xp === 0
    const isFastCombo = getIsFastCombo(finishedActivities)

    const { title, subtitle } = useRef(
        getCelebrationMessage(
            champion,
            finishedActivities?.length,
            isFirstActivity,
            isFastCombo
        )
    ).current

    const width =
        dimensions.width >= 800
            ? Math.max(533, dimensions.width / 2)
            : Math.max(400, dimensions.width / 1.5)

    const iconSize = 24
    const fontSize = 14

    const insets = useSafeAreaInsets()

    return (
        <View
            style={{
                flex: 1,
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                paddingHorizontal: 8,
                alignSelf: "center",
                width: dimensions?.width,
                height: "100%",
                position: "absolute",
            }}
        >
            <LottieView
                autoPlay
                speed={1}
                loop={false}
                pointerEvents="none"
                ref={lottieAnimationRef}
                style={{
                    width: "100%",
                    height: "100%",
                    alignSelf: "center",
                    position: "relative",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 0,
                    elevation: 0,
                }}
                resizeMode="cover"
                source={require("../../../../assets/animations/confetti-2.json")}
            />

            <ScrollView
                style={{
                    paddingTop: insets.top,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    elevation: 1,
                }}
                contentContainerStyle={{
                    flexGrow: 1,
                    paddingTop: 20,
                    paddingHorizontal: 20,
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    height:"100%",
                    overflow: "visible",
                }}
                showsVerticalScrollIndicator={false}
            >
                <View style={{ alignItems: "center" }}>
                    <View
                        style={{
                            width: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 12,
                            maxHeight: dimensions?.height * 0.7,
                        }}
                    >
                        <ChampionImageM
                            name={champion}
                            variant="happy"
                            width={dimensions?.width * 0.85}
                            height={dimensions?.height * 0.30}
                            congratMode={true}
                        />
                    </View>

                    <View
                        style={{
                            flexDirection: "column",
                            alignItems: "center",
                            alignContent: "center",
                            paddingTop: 8,
                        }}
                    >
                        <TextM
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            fontSize={36}
                            style={{
                                marginVertical: 4,
                                textAlign: "center",
                            }}
                            wrap
                        >
                            {title}
                        </TextM>
                        <TextM
                            fontWeight="SemiBold"
                            color={theme.colors.grey}
                            fontSize={20}
                            style={{
                                marginVertical: 4,
                                textAlign: "center",
                            }}
                            wrap
                        >
                            {subtitle}
                        </TextM>
                    </View>
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            paddingVertical: 20,
                            marginTop: 20,
                            width: dimensions?.width - 40,
                        }}
                    >
                        {xPGained > 0 && (
                            <CounterWrapper
                                title="XP"
                                borderColor={theme.colors.accent}
                                isVisible={
                                    animationState[COUNTER_TYPES.XP].visible
                                }
                                onAnimationComplete={() =>
                                    handleCounterWrapperComplete(
                                        COUNTER_TYPES.XP
                                    )
                                }
                                style={{
                                    flex: 1,
                                    maxWidth: "33%",
                                    marginHorizontal: 8,
                                }}
                            >
                                <XPCount
                                    count={xPGained}
                                    xPGained={xPGained}
                                    canStart={
                                        animationState[COUNTER_TYPES.XP].visible
                                    }
                                    onAnimationComplete={() =>
                                        handleCounterAnimationComplete(
                                            COUNTER_TYPES.XP
                                        )
                                    }
                                    iconSize={iconSize}
                                    fontSize={fontSize}
                                    heightFirst
                                />
                            </CounterWrapper>
                        )}

                        {petalsGained > 0 && (
                            <CounterWrapper
                                title="Pétales"
                                borderColor={theme.colors.petalNew}
                                isVisible={
                                    animationState[COUNTER_TYPES.PETALS].visible
                                }
                                onAnimationComplete={() =>
                                    handleCounterWrapperComplete(
                                        COUNTER_TYPES.PETALS
                                    )
                                }
                                style={{
                                    flex: 1,
                                    maxWidth: "33%",
                                    marginHorizontal: 8,
                                }}
                            >
                                <PetalCounter
                                    count={petalsGained}
                                    petalsGained={petalsGained}
                                    canStart={
                                        animationState[COUNTER_TYPES.XP]
                                            .completed
                                    }
                                    onAnimationComplete={() =>
                                        handleCounterAnimationComplete(
                                            COUNTER_TYPES.PETALS
                                        )
                                    }
                                    iconSize={iconSize}
                                    fontSize={fontSize}
                                />
                            </CounterWrapper>
                        )}

                        <CounterWrapper
                            title="Chrono"
                            borderColor={theme.colors.chronoBlue}
                            isVisible={
                                animationState[COUNTER_TYPES.CHRONO].visible
                            }
                            style={{
                                flex: 1,
                                maxWidth: "33%",
                                marginHorizontal: 8,
                            }}
                        >
                            <ChronometerCounter
                                time={chronoDuration ?? 0}
                                iconSize={iconSize}
                                fontSize={fontSize}
                            />
                        </CounterWrapper>
                    </View>
                </View>
                <Animated.View
                    style={[
                        {
                            marginBottom: insets.bottom + 8,
                            alignItems: "center",
                        },
                        fadeStyle,
                    ]}
                >
                    <TouchableRippleM
                        autoClickDuration={COUNTDOWN_DURATION * 1000}
                        autoClickColor={theme.colors.primary}
                        autoClickPaused={!timerCanStart}
                        onPress={onContinuePress}
                        style={{
                            width:
                                dimensions?.width < 800
                                    ? dimensions?.width - 40
                                    : dimensions?.width / 2,
                            alignSelf: "center",
                            marginBottom: 10,
                            height: 50,
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 15,
                            elevation: 10,
                        }}
                    >
                        <TextM
                            color={theme.colors.white}
                            fontWeight="ExtraBold"
                            fontSize={16}
                        >
                            {continueLabel}
                        </TextM>
                    </TouchableRippleM>
                    {secondaryActions.map((action) => (
                        <TouchableOpacity
                            key={action.key || action.label}
                            onPress={action.onPress}
                            activeOpacity={0.7}
                            accessibilityRole="button"
                            accessibilityLabel={action.label}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 10,
                                paddingHorizontal: 16,
                            }}
                        >
                            {action.icon ? (
                                <FontAwesome5
                                    name={action.icon}
                                    size={13}
                                    color={theme.colors.primary}
                                    style={{ marginRight: 8 }}
                                    solid
                                />
                            ) : null}
                            <TextM
                                color={theme.colors.primary}
                                fontWeight="Bold"
                                fontSize={14}
                            >
                                {action.label}
                            </TextM>
                        </TouchableOpacity>
                    ))}
                </Animated.View>
            </ScrollView>
        </View>
    )
}

const mapStateToProps = (state) => ({
    challengeTemplates: state.data.staticData?.challenge_templates,
    activityTemplates: state.data.staticData?.activity_templates,
    schoolSubjects: state.data.staticData?.school_subjects,
    user: state.data.currentUser?.object,
    liveSessionId: state.liveMode?.liveSessionId,
    liveSessions: state.data.currentUser?.object?.live_sessions,
    isLiveMode: state.liveMode?.isLiveModeEnabled,
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ActivityFinalizedCongrats)
